import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  PaperPlaneRight, ChatCircle, ArrowsClockwise, Sparkle,
  ArrowSquareOut, BookOpen, Lightbulb,
} from "@phosphor-icons/react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api } from "../lib/kostra";
import { useAuth } from "../lib/auth";

// Lightweight Markdown-ish renderer: bold (**...**), inline code (`...`),
// numbered/bulleted lines, and "Pro Tip:" emphasis.
function renderRich(text) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  return lines.map((line, idx) => {
    if (!line.trim()) return <div key={idx} className="h-2" />;
    const isProTip = /^pro\s*tip\s*:/i.test(line.trim());
    // bold ** **
    let parts = [];
    const re = /\*\*(.+?)\*\*|`([^`]+)`/g;
    let last = 0;
    let m;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) parts.push(line.slice(last, m.index));
      if (m[1]) parts.push(<strong key={`b${m.index}`} className="font-semibold text-[#0A3B24]">{m[1]}</strong>);
      else if (m[2]) parts.push(<code key={`c${m.index}`} className="px-1.5 py-0.5 rounded bg-[#FDF9EE] text-[#A87C0E] text-[12px] font-mono-num border border-[#D4AF37]/30">{m[2]}</code>);
      last = m.index + m[0].length;
    }
    if (last < line.length) parts.push(line.slice(last));
    if (isProTip) {
      return (
        <div key={idx} className="mt-3 p-3 rounded-md border-l-4 border-[#D4AF37] bg-[#FDF9EE] flex gap-2 items-start">
          <Lightbulb size={16} weight="fill" className="text-[#D4AF37] shrink-0 mt-0.5" />
          <div className="text-sm text-[#0A3B24]">{parts.length ? parts : line}</div>
        </div>
      );
    }
    // Numbered list line "1. ..." or bullet "- ..."
    const listMatch = /^(\d+\.)\s+(.+)$/.exec(line.trim()) || /^[-•]\s+(.+)$/.exec(line.trim());
    if (listMatch) {
      return <div key={idx} className="text-sm text-[#111827] leading-relaxed pl-4 -indent-4">• {parts.length ? parts : line.trim().replace(/^[-•]\s+|\d+\.\s+/, "")}</div>;
    }
    return <div key={idx} className="text-sm text-[#111827] leading-relaxed">{parts.length ? parts : line}</div>;
  });
}

const ROUTE_LABELS = {
  "/": "Calculator", "/inspiration": "Recipe Library", "/production": "Production Plan",
  "/rnd": "R&D Lab", "/samples": "Sample Reports", "/discover": "Discovery",
  "/library": "My Recipes", "/askme": "Ask Me", "/guide": "Function Guide",
};

// Simple deep-link extraction from an answer mentioning a tab name
function pickQuickLinks(answer) {
  const links = [];
  const probes = [
    { route: "/discover", labels: ["/discover", "Discovery"] },
    { route: "/rnd", labels: ["/rnd", "R&D Lab"] },
    { route: "/production", labels: ["/production", "Production Plan"] },
    { route: "/inspiration", labels: ["/inspiration", "Recipe Library"] },
    { route: "/library", labels: ["/library", "My Recipes"] },
    { route: "/samples", labels: ["/samples", "Sample"] },
    { route: "/", labels: ["Calculator", " Summary tab", "Ingredients tab"] },
    { route: "/guide", labels: ["Function Guide", "/guide"] },
  ];
  for (const p of probes) {
    if (p.labels.some((l) => answer.toLowerCase().includes(l.toLowerCase()))) {
      links.push({ route: p.route, label: ROUTE_LABELS[p.route] || p.route });
      if (links.length >= 3) break;
    }
  }
  return links;
}

export default function AskMePage() {
  const { isAdmin, openLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [sessionId] = useState(
    () => localStorage.getItem("kostra:askme:session") ||
          (() => {
            const s = `s_${crypto.randomUUID().slice(0, 12)}`;
            localStorage.setItem("kostra:askme:session", s); return s;
          })(),
  );
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text, time, links?}
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/assistant/suggestions").then(({ data }) => setSuggestions(data.items || []))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, pending]);

  const ask = async (text) => {
    const message = (text ?? input).trim();
    if (!message) return;
    if (!isAdmin) return openLogin();
    setMessages((m) => [...m, { role: "user", text: message, time: Date.now() }]);
    setInput("");
    setPending(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        session_id: sessionId,
        message,
        current_route: location.pathname,
      });
      const ans = data.answer || "";
      setMessages((m) => [...m, {
        role: "assistant", text: ans, time: Date.now(),
        links: pickQuickLinks(ans),
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Coach is busy — retry");
      setMessages((m) => [...m, {
        role: "assistant", text: "Sorry, I had trouble answering that. Please try again in a moment.",
        time: Date.now(),
      }]);
    } finally { setPending(false); }
  };

  const reset = async () => {
    // Optimistically clear UI — don't depend on API success
    setMessages([]);
    try {
      await api.post("/assistant/reset", { session_id: sessionId });
      toast.success("Conversation reset");
    } catch {
      toast.success("Started a fresh chat");
    }
  };

  // Guest CTA -------------------------------------------------------------
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 text-center">
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2 justify-center">
              <ChatCircle size={14} weight="fill" /> KOSTRA Coach
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-medium mt-3 leading-tight">
              Your AI assistant <br className="hidden md:block" />
              <span className="text-[#D4AF37]">for the entire platform.</span>
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-3 max-w-2xl mx-auto">
              Ask anything about KOSTRA — costing formulas, R&amp;D optimization, pricing strategy.
              Admin login required.
            </p>
            <Button onClick={() => openLogin()} className="mt-6 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
                    data-testid="askme-guest-login-btn">
              Admin login to chat with KOSTRA Coach
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Header />

      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8 pt-8 pb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
                <ChatCircle size={14} weight="fill" /> KOSTRA Coach
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-medium mt-2 leading-tight">
                Ask me anything <br className="hidden md:block" />
                <span className="text-[#D4AF37]">about your kitchen, costs and recipes.</span>
              </h1>
              <p className="text-white/70 text-sm mt-2 max-w-xl">
                I know every KOSTRA feature, every formula, and a few decades of restaurant economics.
              </p>
            </div>
            <Button onClick={reset} variant="outline"
                    className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white font-medium h-10"
                    data-testid="askme-reset-btn">
              <ArrowsClockwise size={14} weight="bold" className="mr-1.5" /> New chat
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-5 md:px-8 -mt-6 pb-12">
        <div className="kostra-card p-0 overflow-hidden flex flex-col h-[calc(100vh-260px)] min-h-[500px]"
             data-testid="askme-chat">
          {/* Suggested prompts (only when empty) */}
          {messages.length === 0 && (
            <div className="p-6 md:p-8 border-b border-[#E5E7EB] bg-gradient-to-b from-[#FDF9EE]/40 to-white">
              <div className="text-xs uppercase tracking-[0.2em] text-[#0A3B24] font-bold inline-flex items-center gap-1.5">
                <Sparkle size={11} weight="fill" className="text-[#D4AF37]" /> Try one of these
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestions.map((s, i) => (
                  <button key={i} type="button" onClick={() => ask(s.q)}
                          className="text-left p-3 rounded-lg border-2 border-[#E5E7EB] bg-white hover:border-[#D4AF37] hover:bg-[#FDF9EE]/40 transition group"
                          data-testid={`askme-suggestion-${i}`}>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#A87C0E] mb-1">{s.tag}</div>
                    <div className="text-sm text-[#111827] leading-snug group-hover:text-[#0A3B24]">{s.q}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Conversation */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 md:p-8 space-y-6"
               data-testid="askme-messages">
            <AnimatePresence>
              {messages.map((m, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                  data-testid={`askme-msg-${m.role}-${i}`}
                >
                  <div className={
                    m.role === "user"
                      ? "max-w-[78%] px-4 py-2.5 rounded-2xl rounded-br-md bg-[#0A3B24] text-white text-sm shadow-sm"
                      : "max-w-[88%] px-5 py-4 rounded-2xl rounded-bl-md bg-white border-2 border-[#FDF9EE] shadow-sm"
                  }>
                    {m.role === "user" ? m.text : (
                      <>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-2 inline-flex items-center gap-1.5">
                          <Sparkle size={10} weight="fill" /> KOSTRA Coach
                        </div>
                        <div className="space-y-1">{renderRich(m.text)}</div>
                        {(m.links || []).length > 0 && (
                          <div className="mt-4 pt-3 border-t border-[#F1E9D0] flex flex-wrap gap-2">
                            {m.links.map((l) => (
                              <Button key={l.route} size="sm" type="button"
                                onClick={() => navigate(l.route)}
                                variant="outline"
                                className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold text-xs"
                                data-testid={`askme-deeplink-${l.route.replace(/\//g, "_")}`}>
                                <ArrowSquareOut size={12} weight="bold" className="mr-1" />
                                Open {l.label}
                              </Button>
                            ))}
                            <Button size="sm" type="button"
                              onClick={() => navigate("/guide")}
                              variant="outline"
                              className="border-[#D4AF37] text-[#A87C0E] hover:bg-[#FDF9EE] font-semibold text-xs">
                              <BookOpen size={12} weight="bold" className="mr-1" /> Learn more in Guide
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
              {pending && (
                <motion.div key="pending" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl bg-white border-2 border-[#FDF9EE] inline-flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#D4AF37] animate-ping" />
                    <span className="text-sm text-[#4B5563]">KOSTRA Coach is thinking…</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Composer */}
          <div className="p-4 md:p-5 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault(); ask();
                  }
                }}
                placeholder="Ask anything about the calculator, R&D, pricing or recipes…"
                rows={2}
                className="resize-none min-h-[52px]"
                data-testid="askme-input"
              />
              <Button onClick={() => ask()} disabled={pending || !input.trim()}
                      className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-[52px] px-5"
                      data-testid="askme-send-btn">
                <PaperPlaneRight size={16} weight="bold" />
              </Button>
            </div>
            <div className="text-[10px] text-[#4B5563] mt-2">
              Powered by GPT-4o-mini · Press <kbd className="font-mono-num font-bold text-[#0A3B24]">Enter</kbd> to send,
              <kbd className="font-mono-num font-bold text-[#0A3B24]"> Shift+Enter</kbd> for newline
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
